import { Banknote, Clock3, ShieldCheck } from "lucide-react";
import Reveal from "@/components/Reveal";
import { CONTENT } from "@/content";

const ETAPES = [
  {
    num: "01",
    title: "L'accueil & la fiche patient",
    text: "À votre arrivée, nous complétons ensemble votre fiche : informations personnelles, historique médical, pathologies, antécédents et traitements en cours.",
  },
  {
    num: "02",
    title: "L'anamnèse",
    text: "Un temps d'échange approfondi autour de votre motif de consultation, pour comprendre l'origine de la douleur, son contexte et son lien avec votre mode de vie.",
  },
  {
    num: "03",
    title: "Les tests & le traitement",
    text: "Tests de mobilité et palpation fine, puis corrections ostéopathiques douces et adaptées à votre corps et à votre sensibilité.",
  },
  {
    num: "04",
    title: "Le bilan de fin de séance",
    text: "Nous faisons le point ensemble : réponses à vos questions, conseils personnalisés et exercices pour prolonger les bienfaits de la séance chez vous.",
  },
];

const Tarifs = () => (
  <section id="tarifs" className="grain bg-beige-cream py-28 md:py-36" data-testid="tarifs-section">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-12">
      <div className="lg:col-span-7">
        <Reveal>
          <p className="text-xs font-medium uppercase tracking-[0.28em] text-gold">La séance</p>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight text-anthracite md:text-5xl">
            Votre première consultation, <em className="text-sage-dark">pas à pas</em>
          </h2>
          <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-taupe">
            Une heure rien qu'à vous. Voici comment se déroule une séance au
            cabinet, de votre arrivée au bilan final.
          </p>
        </Reveal>

        <div className="mt-12">
          {ETAPES.map((e, i) => (
            <Reveal key={e.num} delay={i * 0.08}>
              <div
                data-testid={`tarifs-etape-${e.num}`}
                className="group grid grid-cols-[auto_1fr] gap-8 border-t border-sage-light/60 py-9 first:border-t-0 first:pt-0"
              >
                <span className="font-serif text-4xl font-light text-sage-light transition-colors duration-500 group-hover:text-gold md:text-5xl">
                  {e.num}
                </span>
                <div>
                  <h3 className="font-serif text-xl font-light italic text-anthracite md:text-2xl">{e.title}</h3>
                  <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-taupe md:text-base">{e.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="lg:col-span-5">
        <Reveal delay={0.1} className="lg:sticky lg:top-32">
          <div className="rounded-xl border border-sage-light/60 bg-ivory p-10 md:p-12" data-testid="tarifs-card">
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-taupe/70">Consultation d'ostéopathie</p>
            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-serif text-6xl font-light text-anthracite" data-testid="tarifs-prix">60&nbsp;€</span>
              <span className="text-sm font-light text-taupe">la séance</span>
            </div>
            <div className="gold-rule mt-8" />
            <ul className="mt-8 space-y-5">
              <li className="flex items-start gap-4" data-testid="tarifs-duree">
                <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-sage-dark" strokeWidth={1} />
                <span className="text-sm font-light leading-relaxed text-taupe">
                  Durée : <span className="text-anthracite">environ 1 heure</span>, première consultation ou suivi
                </span>
              </li>
              <li className="flex items-start gap-4" data-testid="tarifs-paiement">
                <Banknote className="mt-0.5 h-5 w-5 shrink-0 text-sage-dark" strokeWidth={1} />
                <span className="text-sm font-light leading-relaxed text-taupe">
                  Règlement par <span className="text-anthracite">chèque ou espèces</span> uniquement — la carte bancaire n'est pas acceptée
                </span>
              </li>
              <li className="flex items-start gap-4" data-testid="tarifs-mutuelle">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-sage-dark" strokeWidth={1} />
                <span className="text-sm font-light leading-relaxed text-taupe">
                  De nombreuses mutuelles remboursent tout ou partie des séances — une facture vous est remise
                </span>
              </li>
            </ul>
            <a
              data-testid="tarifs-booking-button"
              href={CONTENT.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-shine mt-10 block rounded-md bg-sage px-8 py-4 text-center text-sm font-medium uppercase tracking-[0.14em] text-ivory transition-colors duration-300 hover:bg-sage-dark"
            >
              Prendre rendez-vous
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default Tarifs;
